import { Link } from 'react-router-dom';

const features = [
  {
    icon: 'ri-search-eye-line',
    title: 'Faster Product Discovery',
    desc: 'Stop wasting hours scrolling through endless listings. Our Sugargoo Spreadsheet organizes products by category so you can find exactly what you need in seconds.',
  },
  {
    icon: 'ri-scales-3-line',
    title: 'Compare Products Easily',
    desc: 'Side-by-side product comparison helps you evaluate quality, pricing, and style before making purchasing decisions on the Sugargoo platform.',
  },
  {
    icon: 'ri-fire-line',
    title: 'Find Trending Items',
    desc: 'Stay ahead of the curve with our regularly updated collection of trending and popular finds across all major fashion and lifestyle categories.',
  },
  {
    icon: 'ri-image-line',
    title: 'Understand Product Quality',
    desc: 'Make informed decisions with detailed product information. Know what to expect before your items arrive at the Sugargoo warehouse.',
  },
  {
    icon: 'ri-shield-check-line',
    title: 'Reduce Shopping Mistakes',
    desc: 'Our organized database helps you avoid common pitfalls — wrong sizes, misleading listings, and overpriced items that don\'t deliver on quality.',
  },
  {
    icon: 'ri-guide-line',
    title: 'Expert Shopping Guides',
    desc: 'Access comprehensive guides that walk you through every step of the Sugargoo shopping journey, from product discovery to final delivery.',
  },
];

export default function AboutSection() {
  return (
    <section className="w-full px-4 md:px-6 py-12 md:py-16 bg-background-100">
      <div className="max-w-6xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-14 items-start">
          <div>
            <p className="font-heading text-xs md:text-sm uppercase tracking-widest text-accent-600 font-semibold mb-3">
              Why Sugargoo Spreadsheet
            </p>
            <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 mb-5 leading-tight">
              What Is the Sugargoo Spreadsheet?
            </h2>
            <div className="space-y-4 text-foreground-600 text-sm md:text-base leading-relaxed">
              <p>
                The <strong className="text-foreground-900">Sugargoo Spreadsheet</strong> is a carefully curated product research database designed to help online shoppers discover trending items, compare products across categories, and organize their purchases before using Sugargoo&apos;s shopping and shipping services.
              </p>
              <p>
                Unlike traditional product search where you scroll through endless pages of unorganized listings, the Sugargoo Spreadsheet categorizes products into intuitive groups — from sneakers and streetwear to accessories and electronics — making it dramatically easier to find what you are looking for.
              </p>
              <p>
                Whether you are a first-time Sugargoo user exploring the platform or an experienced shopper looking to discover new finds, this spreadsheet serves as your go-to research hub, updated weekly with fresh products and categories.
              </p>
            </div>

            <div className="mt-6 p-5 rounded-xl bg-background-50 border border-background-200/70">
              <p className="text-xs md:text-sm text-foreground-600 mb-4 font-medium">
                Traditional Search vs Sugargoo Spreadsheet
              </p>
              <div className="space-y-2.5">
                {[
                  { label: 'Product Discovery', trad: 'Random & Scattered', ss: 'Organized Database' },
                  { label: 'Research Time', trad: 'Hours of Browsing', ss: 'Minutes with Filters' },
                  { label: 'Category Browsing', trad: 'Difficult & Messy', ss: 'Easy & Structured' },
                  { label: 'Product Comparison', trad: 'Manual & Tedious', ss: 'Side-by-Side View' },
                ].map((row) => (
                  <div key={row.label} className="flex items-center gap-3 text-xs md:text-sm">
                    <span className="w-1/3 text-foreground-700 font-medium">{row.label}</span>
                    <span className="w-[30%] text-foreground-400 line-through">{row.trad}</span>
                    <i className="ri-arrow-right-line text-foreground-300 text-xs"></i>
                    <span className="flex-1 text-primary-600 font-medium">{row.ss}</span>
                  </div>
                ))}
              </div>
            </div>

            <Link
              to="/article/sugargoo-spreadsheet-complete-guide"
              className="inline-flex items-center gap-2 mt-6 px-6 py-3 rounded-full bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              Read Full Guide <i className="ri-arrow-right-line"></i>
            </Link>
          </div>

          <div>
            <div className="relative rounded-2xl overflow-hidden mb-6">
              <img
                src="https://readdy.ai/api/search-image?query=Stylish%20flatlay%20of%20curated%20fashion%20pieces%20including%20sneakers%20and%20accessories%20on%20warm%20cream%20linen%20surface%2C%20soft%20diffused%20natural%20window%20light%2C%20organized%20product%20research%20layout%20with%20notebook%20nearby%2C%20editorial%20minimalist%20photography%2C%20calm%20warm%20neutral%20tones&width=800&height=900&seq=sugargoo-about-img-v2&orientation=portrait"
                alt="Organized Sugargoo Spreadsheet product research layout"
                className="w-full aspect-[4/5] object-cover rounded-2xl"
                title="Sugargoo Spreadsheet organized product research"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {features.map((feature) => (
                <div key={feature.title} className="p-4 rounded-xl bg-background-50 border border-background-200/60 hover:border-accent-200 transition-colors">
                  <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 mb-2.5">
                    <i className={`${feature.icon} text-base`}></i>
                  </div>
                  <h4 className="font-heading text-sm font-semibold text-foreground-900 mb-1.5">{feature.title}</h4>
                  <p className="text-xs text-foreground-600 leading-relaxed">{feature.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}